import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Banner } from "@/components/ui/banner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/auth-context";
import { EVALUATION_QUERY_KEY } from "@/hooks/queries/useGetEvaluation";
import { EVALUATIONS_QUERY_KEY } from "@/hooks/queries/useListEvaluations";
import {
  claimUnattributedEvaluations,
  listUnattributedEvaluationIds,
} from "@/lib/owner-scope";

// Franklin#861 / DW-907 — the reachable door for never-synced unattributed drafts.
//
// Mounted once under AuthProvider (App.tsx). When an authenticated session finds local
// drafts the v3 upgrade left ownerless, it asks the clinician to claim them explicitly.
// Yes → adopt via `claimUnattributedEvaluations`. No / dismiss → leave the rows alone and
// skip for THIS userId for the rest of the page load (so B declining does not permanently
// close the door for A on a later sign-in). Never auto-adopts — that is D9's rejected option.

/** Session-scoped "already asked / declined" set. Module-level so a remount mid-session
 *  does not re-prompt; clears on full reload. Exported for tests only. */
const skippedUserIds = new Set<string>();

const CLAIM_FAILURE_MESSAGE =
  "Something went wrong saving locally. The drafts are still here — try again.";

/** Test helper — drop the session skip set between cases. */
export function resetUnattributedDraftClaimSkipForTests(): void {
  skippedUserIds.clear();
}

export function UnattributedDraftClaimGate() {
  const { status, user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [count, setCount] = React.useState(0);
  const [claiming, setClaiming] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const userId = user?.userId;

  React.useEffect(() => {
    if (status !== "authenticated" || !userId) {
      setOpen(false);
      setError(null);
      return;
    }
    if (skippedUserIds.has(userId)) {
      setOpen(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      const ids = await listUnattributedEvaluationIds();
      if (cancelled) return;
      if (ids.length === 0 || skippedUserIds.has(userId)) {
        setOpen(false);
        setCount(0);
        setError(null);
        return;
      }
      setCount(ids.length);
      setError(null);
      setOpen(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [status, userId]);

  const skipForSession = React.useCallback(() => {
    if (userId) skippedUserIds.add(userId);
    setError(null);
    setOpen(false);
  }, [userId]);

  const onClaim = React.useCallback(async () => {
    if (!userId || claiming) return;
    setClaiming(true);
    setError(null);
    try {
      await claimUnattributedEvaluations(userId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [EVALUATIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [EVALUATION_QUERY_KEY] }),
      ]);
      skippedUserIds.add(userId);
      setOpen(false);
      setCount(0);
    } catch {
      // Keep the dialog open and do NOT session-skip — clinician can retry. Partial mid-loop
      // adopts are possible (each eval is its own Dexie txn); retry is idempotent for already-
      // claimed ids and continues the remainder.
      setError(CLAIM_FAILURE_MESSAGE);
    } finally {
      setClaiming(false);
    }
  }, [userId, claiming, queryClient]);

  const draftWord = count === 1 ? "draft" : "drafts";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) skipForSession();
      }}
    >
      <DialogContent
        className="w-[min(var(--width-md,520px),100%)]"
        data-testid="unattributed-draft-claim-gate"
      >
        <DialogHeader>
          <DialogTitle>Local drafts without an owner</DialogTitle>
          <DialogDescription>
            This device holds {count} local evaluation {draftWord} that were saved offline and
            never reached the server. They have no owner stamp, so they stay hidden until someone
            claims them. Confirm only if this is your work — claiming assigns these drafts to
            your account on this device.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <Banner variant="danger" title="Couldn’t claim">
            {error}
          </Banner>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={skipForSession} disabled={claiming}>
            Not mine
          </Button>
          <Button type="button" variant="primary" onClick={() => void onClaim()} disabled={claiming}>
            These are mine
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
